// --------------------------------------------------------------
// Created On: 2024-06-04
// Author: Dimitra Weinstein
//
// Last Modified: 2026-01-09
// Modified By: Dimitra Weinstein
// Copyright 2024 - 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useMemo, useState } from "react";
import { MAX_ITEM_QUANTITY, MAX_IMPELLER_ANGLE, MAX_IMPELLER_TRIM } from "../../../constants/miscellaneous";
import ModalBody from "../../ModalBody/ModalBody";
import LeadTime from "../../LeadTime/LeadTime";
import ColorPicker from "../../ColorPicker/ColorPicker";
import styles from "./PartInfo.module.scss";

// Single customizable part form.
export default function PartInfo(props: Props): Component {
  const selectedPaintColor = useMemo(
    () => getPaintColor(props.paintColors, props.selectedPaintColorId),
    [JSON.stringify(props.paintColors), props.selectedPaintColorId]
  );
  const previousPartMaterial = useMemo(
    () => props.materialParts.find((materialPart) => materialPart.partNumber === props.partNumber) || "",
    [JSON.stringify(props.materialParts), props.partNumber]
  );
  const [isFullTrim, setIsFullTrim] = useState<boolean>(true);
  const [selectedPartMaterial, setSelectedPartMaterial] = useState<string>("");

  // Get the selected paint color.
  function getPaintColor(paintColors: PaintColor[], selectedPaintColorId: number | null): PaintColor | null {
    const paintColorIndex = paintColors.findIndex((paintColor) => paintColor.paintColorId === selectedPaintColorId);
    if (paintColorIndex > -1) {
      return paintColors[paintColorIndex];
    } else {
      return null;
    }
  }

  // Get discounted price of part.
  function getDiscountedPrice(price: number | null, companyDiscount?: number | null): string {
    if (price === null) {
      return "N/A";
    } else if (companyDiscount === null || companyDiscount === undefined) {
      return price.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    } else {
      return (price - price * (companyDiscount / 100)).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }
  }

  return (
    <ModalBody>
      <div data-test="part-info-modal-item" className={`row mx-3 my-3 ${styles.modalForm}`}>
        <div className="col">
          <div className="row mx-3 my-3">
            <div className="col">
              <label data-test="part-description" className={`row form-label text-start ${styles.partDescription}`}>
                {props.description}
              </label>
              <p data-test="part-number" className="row small">
                {props.partNumber}
              </p>
            </div>
            <div className="col text-center">
              <label className="row justify-content-center form-label font-weight-bold">
                Quantity ({props.unitName})
              </label>
              <input
                className={"text-center form-control"}
                data-test="item-quantity-input"
                type="text"
                maxLength={10}
                max={MAX_ITEM_QUANTITY}
                value={props.orderQuantity}
                onChange={(e) => props.onQuantityChange(props.partId, e.target.value)}
                onWheel={(e) => e.currentTarget.blur()}
              />
            </div>
            <div className="col text-center">
              <label className="row justify-content-center form-label font-weight-bold">List Price</label>
              {props.price !== null ? (
                <p data-test="price-per-each" className="row justify-content-center">
                  &#36;
                  {props.price.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </p>
              ) : (
                <p data-test="price-per-each" className="row justify-content-center">
                  N/A
                </p>
              )}
            </div>
            <div className="col text-center">
              <label className="row justify-content-center form-label font-weight-bold">Weight</label>
              {props.weight !== null && props.weight !== 0 ? (
                <p data-test="part-weight" className="row justify-content-center">
                  {props.weight}&nbsp;lbs
                </p>
              ) : (
                <p data-test="part-weight" className="row justify-content-center">
                  N/A
                </p>
              )}
            </div>
            <div className="col text-center">
              <label className="row justify-content-center form-label font-weight-bold">Discount Price</label>
              {props.price !== null ? (
                <p data-test="price-per-each-discount" className="row justify-content-center">
                  &#36;{getDiscountedPrice(props.price, props.companyDiscount)}
                </p>
              ) : (
                <p data-test="price-per-each-discount" className="row justify-content-center">
                  N/A
                </p>
              )}
            </div>
            <div data-test="ready-to-ship" className="col text-center">
              <label className="row justify-content-center form-label font-weight-bold">Ready To Ship</label>
              <LeadTime
                className="justify-content-center pt-2"
                partNumber={props.partNumber}
                quantity={props.orderQuantity.toString()}
                onLeadTimeCalculated={() => {}}
                paintColorId={props.isPaintable ? props.selectedPaintColorId : null}
                isImpeller={props.isImpeller}
                impellerTrim={props.impellerTrim}
                impellerAngle={props.impellerAngle}
              />
            </div>
          </div>
          <div className={props.isImpeller ? "row mx-3 my-3 justify-content-around" : "row mx-3 my-3"}>
            {props.isPaintable && (
              <div className="col-auto mx-2">
                <label className="form-label font-weight-bold">Paint Color</label>
                <div className={styles.colorPickerWrapper}>
                  <ColorPicker
                    colors={props.paintColors}
                    selectedColorId={selectedPaintColor ? selectedPaintColor.paintColorId : null}
                    onChange={(selectedPaintColorId) => props.onPaintColorChange(selectedPaintColorId)}
                  />
                </div>
              </div>
            )}
            {!props.partOnlyAvailableInOneMaterial &&
              !props.isAddAllOrderableParts &&
              props.materialParts.length > 1 && (
                <div className="col-auto mx-2 text-center">
                  <label className="form-label font-weight-bold">Change Material</label>
                  <div
                    className={`${styles.changeMaterialWrapper} row justify-content-center align-items-center mx-auto pb-2`}
                  >
                    <input
                      className="form-check-input form-check-big text-center mx-1 py-0"
                      type="checkbox"
                      data-test="change-material-checkbox"
                      checked={props.changeMaterialBoxIsChecked}
                      onChange={(e) => {
                        props.onChangeMaterialBoxIsCheckedChange(e.target.checked ? true : false),
                          setSelectedPartMaterial(previousPartMaterial ? previousPartMaterial.partNumber : "");
                      }}
                    />
                    <div className="row justify-content-center mx-4 pt-2">
                      {props.changeMaterialBoxIsChecked && (
                        <select
                          id="change-material-select"
                          className="form-select form-select-md mx-auto mt-1 py-0"
                          value={selectedPartMaterial ? selectedPartMaterial : ""}
                          onChange={(e) => {
                            props.onChangeMaterialBoxIsCheckedChange(true),
                              props.onSelectPartMaterialChange(e.target.value),
                              setSelectedPartMaterial(e.target.value);
                          }}
                        >
                          <option key="default" value="" disabled>
                            Select Material
                          </option>
                          {props.materialParts.map((partMaterial, i) => {
                            return (
                              <option key={i} value={partMaterial.partNumber}>
                                {partMaterial.materialName}
                              </option>
                            );
                          })}
                        </select>
                      )}
                    </div>
                  </div>
                </div>
              )}
            {props.isImpeller && (
              <div className="col-3">
                <label className="row justify-content-center form-label font-weight-bold">
                  Impeller Angle (degrees)
                </label>
                <div className="row justify-content-center">
                  <input
                    className="text-center form-control"
                    data-test="impeller-angle-input"
                    type="text"
                    maxLength={10}
                    max={MAX_IMPELLER_ANGLE}
                    value={props.impellerAngle === null ? "" : props.impellerAngle}
                    onChange={(e) => props.onImpellerAngleChange(e.target.value)}
                    onWheel={(e) => e.currentTarget.blur()}
                  />
                </div>
              </div>
            )}
            {props.isImpeller && (
              <div className="col-3">
                <label className="row justify-content-center form-label font-weight-bold">Impeller Trim (inches)</label>
                <div className="row justify-content-center">
                  <select
                    className="form-select form-control my-1"
                    value={isFullTrim ? "full-trim" : "custom"}
                    onChange={(e) => {
                      setIsFullTrim(e.target.value === "full-trim"), props.onImpellerTrimChange(e.target.value);
                    }}
                  >
                    <option value="full-trim">Full Trim</option>
                    <option value="custom">Custom</option>
                  </select>
                  {!isFullTrim && (
                    <input
                      className="text-center form-control"
                      data-test="impeller-trim-input"
                      type="text"
                      maxLength={10}
                      max={MAX_IMPELLER_TRIM}
                      value={props.impellerTrim === "custom" || props.impellerTrim === null ? "" : props.impellerTrim}
                      onChange={(e) => props.onImpellerTrimChange(e.target.value)}
                      onWheel={(e) => e.currentTarget.blur()}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </ModalBody>
  );
}

interface Props {
  partId: number | null;
  partNumber: string;
  description: string;
  price: number | null;
  companyDiscount?: number | null;
  orderQuantity: string;
  unitName: string;
  isImpeller: boolean;
  isPaintable: boolean;
  isSellable: boolean;
  hasOtherMaterial: boolean;
  changeMaterialBoxIsChecked: boolean;
  materialParts: MaterialPart[];
  selectedPaintColorId: number | null;
  defaultPaintColorId: number | null;
  paintColors: PaintColor[];
  impellerAngle: string | null;
  impellerTrim: string | null;
  weight: number | null;
  isAddAllOrderableParts: boolean;
  partOnlyAvailableInOneMaterial: boolean;
  previousPartMaterial: Part | null;
  onChangeMaterialBoxIsCheckedChange: (checked: boolean) => void;
  onSelectPartMaterialChange: (partNumber: string) => void;
  onQuantityChange: (partId: number | null, quantity: string) => void;
  onPaintColorChange: (selectedPaintColorId: number | null) => void;
  onImpellerAngleChange: (impellerAngle: string) => void;
  onImpellerTrimChange: (impellerTrim: string) => void;
}

interface PaintColor {
  paintColorId: number;
  hexCode: string;
  name: string;
}

interface MaterialPart {
  partId: number;
  partNumber: string;
  materialName: string;
  description: string;
  price: number;
  weight: number;
}

interface BomMapGetPart {
  partId: number;
  partNumber: string;
  description: string;
  unitId: number;
  units: string;
  price: number | null;
  companyDiscount?: number | null;
  buildQuantity: string;
  weight: number;
  isPaintable: boolean;
  isSellable: boolean;
  isImpeller: boolean;
  hasOtherMaterial: boolean;
  parts: BomMapGetPart[];
}

interface Part {
  partId: number;
  partNumber: string;
  description: string;
  unitId: number;
  units: string;
  price: number;
  companyDiscount?: number | null;
  buildQuantity: string;
  paintColorId: number | null;
  orderQuantity: string;
  weight: number;
  isPaintable: boolean;
  isSellable: boolean;
  isImpeller: boolean;
  impellerTrim: string | null;
  impellerAngle: string | null;
  hasOtherMaterial: boolean;
  changeMaterialBoxIsChecked: boolean;
  parts: BomMapGetPart[];
}
